<script setup>
import { computed, h } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import wootConstants from 'dashboard/constants/globals';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { useImpersonation } from 'dashboard/composables/useImpersonation';

import {
  DropdownContainer,
  DropdownBody,
  DropdownSection,
  DropdownItem,
} from 'next/dropdown-menu/base';
import Button from 'next/button/Button.vue';
import ToggleSwitch from 'dashboard/components-next/switch/Switch.vue';

const { t } = useI18n();
const store = useStore();
const currentUserAvailability = useMapGetter('getCurrentUserAvailability');
const currentAccountId = useMapGetter('getCurrentAccountId');
const currentUserAcceptsChat = useMapGetter('getCurrentUserAcceptsChat');

const { isImpersonating } = useImpersonation();

const { AVAILABILITY_STATUS_KEYS } = wootConstants;

const statusColors = ['bg-n-teal-9', 'bg-n-amber-9', 'bg-n-slate-9'];

const allAvailabilityStatuses = computed(() =>
  [
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.ONLINE'),
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.BUSY'),
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.OFFLINE'),
  ].map((statusLabel, index) => ({
    label: statusLabel,
    value: AVAILABILITY_STATUS_KEYS[index],
    color: statusColors[index],
    icon: h('span', { class: [statusColors[index], 'size-[12px] rounded'] }),
    active: currentUserAvailability.value === AVAILABILITY_STATUS_KEYS[index],
  }))
);

const availabilityStatuses = computed(() =>
  allAvailabilityStatuses.value.filter(status => status.value !== 'busy')
);

const activeStatus = computed(() => {
  return (
    allAvailabilityStatuses.value.find(status => status.active) ||
    availabilityStatuses.value[0]
  );
});

const acceptsChatToggle = computed({
  get: () => currentUserAcceptsChat.value !== false,
  set: acceptsChat => {
    store.dispatch('updateAcceptsChat', {
      accountId: currentAccountId.value,
      acceptsChat,
    });
  },
});

function changeAvailabilityStatus(availability) {
  if (isImpersonating.value) {
    useAlert(t('PROFILE_SETTINGS.FORM.AVAILABILITY.IMPERSONATING_ERROR'));
    return;
  }
  try {
    store.dispatch('updateAvailability', {
      availability,
      account_id: currentAccountId.value,
    });
  } catch (error) {
    useAlert(t('PROFILE_SETTINGS.FORM.AVAILABILITY.SET_AVAILABILITY_ERROR'));
  }
}
</script>

<template>
  <DropdownSection>
    <div class="grid gap-0 wlab-profile-menu-status">
      <DropdownItem preserve-open>
        <div class="flex-grow flex items-center gap-1">
          <span class="wlab-pm-status-title">{{
            $t('SIDEBAR.SET_YOUR_AVAILABILITY')
          }}</span>
        </div>
        <DropdownContainer :teleport="false">
          <template #trigger="{ toggle }">
            <Button
              size="sm"
              color="slate"
              variant="faded"
              class="min-w-[96px] wlab-pm-availability-btn"
              icon="i-lucide-chevron-down"
              trailing-icon
              @click.stop="toggle"
            >
              <div class="flex gap-1 items-center flex-grow text-sm">
                <div class="p-1 flex-shrink-0">
                  <div class="size-2 rounded-sm" :class="activeStatus.color" />
                </div>
                <span>{{ activeStatus.label }}</span>
              </div>
            </Button>
          </template>
          <DropdownBody class="min-w-32 z-20 js-nested-dropdown">
            <DropdownItem
              v-for="status in availabilityStatuses"
              :key="status.value"
              :label="status.label"
              :icon="status.icon"
              class="cursor-pointer"
              @click="changeAvailabilityStatus(status.value)"
            />
          </DropdownBody>
        </DropdownContainer>
      </DropdownItem>

      <DropdownItem preserve-open>
        <div class="flex-grow flex items-center gap-1">
          <span class="wlab-pm-status-title">Accepts chat</span>
        </div>
        <ToggleSwitch v-model="acceptsChatToggle" />
      </DropdownItem>
    </div>
  </DropdownSection>
</template>
